<template>
  <!-- global container for the table -->
  <div :class="tableClasses">
    <!-- table headers -->
    <div class="flex flex-col md:flex-row md:items-center md:mb-5">
      <!-- label -->
      <div v-show="label" class="font-bold my-2 md:my-0">
        {{ label }}
      </div>

      <div class="flex-auto" />

      <!-- search -->
      <r-text-field
        v-if="!noSearch"
        :value="searchQuery"
        @input="onSearchChange"
        class="mb-3 mt-6 md:mt-0 md:mb-0"
        :placeholder="$t('globals.search')"
      />

      <!-- new user mobile view -->
      <r-button
        v-if="!noNew"
        class="md:hidden shadow-md md:shadow-none fixed md:relative right-0 mr-6 mb-8 mt-3 rounded-full"
        @click="$emit('new-item-req')"
      >
        <font-awesome-icon icon="user-plus" />
      </r-button>

      <!-- export csv mobile view -->
      <r-button
        v-if="isExportCsvVisible"
        color="error"
        class="md:hidden shadow-md md:shadow-none fixed md:relative right-0 mb-8 mt-3 rounded-full"
        style="margin-right: 85px"
        @click="onExportCsv"
      >
        <font-awesome-icon icon="file-export" />
      </r-button>

      <!-- add new attribute -->
      <r-button
        v-if="!noNew"
        class="md:ml-6 hidden md:block shadow-md md:shadow-none fixed md:relative bottom-0 right-0 mr-3 mb-8 mt-2 md:m-0"
        @click="$emit('new-item-req')"
      >
        {{ $t("usersManagement.table.addNew") }}
      </r-button>

      <!-- Table Dropdown -->
      <div
        v-if="dropdownOptions.length"
        class="ml-4 cursor-pointer hidden md:block"
        @click="openMenu"
      >
        <font-awesome-icon icon="ellipsis-v" size="lg" />
        <r-dropdown v-model="isMenuVisible">
          <!-- Load dropdown options -->
          <div v-for="(option, i) in dropdownOptions" :key="i">
            <div
              @click="option.callback"
              class="flex flex-row px-4 py-2 text-gray-800 hover:bg-r-primary hover:text-white cursor-pointer"
            >
              <div class="w-8">
                <font-awesome-icon icon="file-export" class="mr-3" />
              </div>
              <div>{{ option.label }}</div>
            </div>
          </div>
        </r-dropdown>
      </div>
    </div>

    <!-- table content -->
    <table
      v-if="displayRows.length"
      class="table-auto md:table-fixed mb-10 w-full text-left leading-loose"
    >
      <!-- headers -->
      <thead>
        <tr>
          <th v-for="(header, i) in headers" :key="i" :class="header.classes">
            {{ $t(header.label) }}
          </th>
        </tr>
      </thead>

      <!-- body -->
      <!-- rows -->
      <tbody v-if="displayRows.length">
        <slot name="rows" :items="displayRows" />
      </tbody>
    </table>

    <!-- no users founded -->
    <div v-else class="flex flex-col r-table justify-center w-full h-full mb-6">
      <r-icon :icon="emptyItemsIcon" large />
      <span class="text-gray-500 text-center text-lg">
        {{ $t(emptyItemsLabel) }}
      </span>
    </div>

    <!-- Pagination -->
    <div
      class="w-full flex flex-row h-10 absolute bottom-0 left-0"
      v-if="displayRows.length"
    >
      <!-- right arrow -->
      <div class="w-full">
        <div
          v-show="page !== 1"
          @click="prevPage"
          class="flex justify-start cursor-pointer"
        >
          <r-icon icon="angle-left" color="black" />
        </div>
      </div>

      <!-- page text -->
      <div class="flex flex-wrap w-48 font-bold">
        <div class="flex content-center text-center items-center">
          {{ $t("globals.page") }} {{ page }}/{{ pages.length || 1 }}
        </div>
      </div>

      <!-- left arrow -->
      <div class="w-full">
        <div
          v-show="page < pages.length"
          @click="nextPage"
          class="flex justify-end cursor-pointer"
        >
          <r-icon icon="angle-right" color="black" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// timeout wait
const WAIT_INTERVAL = 1000

/**
 * Table component
 */
export default {
  // set component name
  name: "r-table",

  // local state
  data() {
    return {
      // current page
      page: 1,

      // pages index
      pages: [],

      // search text
      searchQuery: "",

      // search timeout typing
      searchTimeout: null,

      // tmp items
      tmpItems: [],

      // menu visible flag
      isMenuVisible: false
    }
  },

  // external properties
  props: {
    // when true removes the new button
    noNew: {
      type: Boolean,
      default: false
    },

    // when true removes the search input
    noSearch: {
      type: Boolean,
      default: false
    },

    // the text for the empty items label
    emptyItemsLabel: {
      type: String,
      default: "usersManagement.table.noUserFound"
    },

    // the icon for the empty items label
    emptyItemsIcon: {
      type: String,
      default: "user-exclamation"
    },

    // rows per page
    rowsPerPage: {
      type: Number,
      default: 5
    },

    // data attributes
    items: {
      type: Array,
      default: () => []
    },

    // dropdown options
    dropdownOptions: {
      type: Array,
      default: () => []
    },

    // table title
    label: {
      type: String,
      default: ""
    },

    // search by array
    searchBy: {
      type: Array,
      default: () => []
    },

    // the headers
    headers: {
      type: Array,
      default: () => []
    }
  },

  /**
   * Set pages on mount
   */
  mounted() {
    this.setPages()
  },

  // computed
  computed: {
    itemsCounter() {
      return this.items.length
    },

    /**
     * Rows to render on the page
     */
    displayRows() {
      return this.paginate()
    },

    /**
     * Table classes
     */
    tableClasses() {
      return {
        "px-2": true,
        "r-table": true,
        "w-full": true,
        relative: true,
        "h-full": true,
        "md:m-0": true
      }
    },

    /**
     * isExportCsvVisible checks if button export
     * is visible
     */
    isExportCsvVisible() {
      return Boolean(
        this.dropdownOptions.length &&
          this.dropdownOptions.find((op) => op.key === "exportCsv")
      )
    }
  },

  // watch attributes
  watch: {
    /**
     * Listen to items changes
     * and set the pages count
     */
    itemsCounter: {
      immediate: true,
      async handler() {
        this.setPages()
      }
    }
  },

  methods: {
    /**
     * Set pages
     */
    setPages() {
      this.pages = []

      // get the number of pages
      let numberOfPages = Math.ceil(this.itemsCounter / this.rowsPerPage)

      // set pages index
      for (let i = 1; i <= numberOfPages; i++) {
        this.pages.push(i)
      }
    },

    /**
     * Paginate the table
     */
    paginate() {
      const to = this.page * this.rowsPerPage
      const from = to - this.rowsPerPage
      return this.items.slice(from, to)
    },

    /**
     * Filter search
     */
    onSearchChange(value) {
      this.searchQuery = value

      // set timeout
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.$emit("on-search", this.searchQuery)
        this.page = 1
      }, WAIT_INTERVAL)
    },

    /**
     * Go to previouse page
     */
    prevPage() {
      this.page -= 1
      this.$emit("prev-page")
    },

    /**
     * Go to next page
     */
    nextPage() {
      this.page += 1
      this.$emit("next-page")
    },

    /**
     * On row delete
     */
    onRowDelete() {
      const data = this.paginate()
      if (data.length === 1) {
        this.prevPage()
      }
    },

    /**
     * open menu
     */
    openMenu() {
      this.isMenuVisible = true
    },

    /**
     * onExportCsv click
     */
    onExportCsv() {
      // find option
      const option = this.dropdownOptions.find((op) => op.key === "exportCsv")

      if (option) {
        option.callback()
      }
    }
  }
}
</script>

<style lang="scss">
@import "../../../style/_main.scss";

.r-table {
  min-height: 100%;
  @include media("<tablet") {
    min-height: 100vh;
  }

  table {
    border-collapse: collapse;

    > tbody > tr:not(:last-child) {
      border-bottom: 1px solid #e2e8f0;
    }
  }
}
</style>
